import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Controller, useForm, SubmitHandler } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import Select, { MultiValue } from 'react-select'

import { Input, InputGroup } from '@components'
import Breadcrumb from '@components/Breadcrumb'
import Button from '@components/Button'
import Divider from '@components/Divider'
import PageTitle from '@components/PageTitle'
import SelectInput, { Option } from '@components/SelectInput'
import Template from '@components/Template'
import TextEditor from '@components/TextEditor'
import { successMessages } from '@constants/feedbackMessages'
import { yupResolver } from '@hookform/resolvers/yup'
import { addQuizSchema } from '@schemas/addQuiz.schema'
import { bancaApi } from '@services/BancaApi'
import { disciplineApi } from '@services/DisciplineApi'
import { filtersApi } from '@services/filtersApi'
import { questionGeneratorApi } from '@services/GenerateQuestions'
import { institutionApi } from '@services/InstitutionApi'
import { occupationApi } from '@services/OccupationApi'
import { yearApi } from '@services/YearApi'
import { openInputModal } from '@store/ducks/modal'
import { notificationCreators } from '@store/ducks/notification'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { inputIsValid } from '@utils/helpers'
import { createBreadcrumbLink, useCreateBreadcrumb } from '@utils/hooks'

type Inputs = {
	disciplineSelect: MultiValue<{ value: number; label: string }>
	subjectSelect: MultiValue<{ value: number; label: string }>
	subSubjectSelect: MultiValue<{ value: number; label: string }>
	yearSelect?: MultiValue<{ value: number; label: string }>
	occupationSelect?: MultiValue<{ value: number; label: string }>
	bancaSelect?: MultiValue<{ value: number; label: string }>
	institutionSelect?: MultiValue<{ value: number; label: string }>
	title: string
	enunciado: string
	comment: string
	trueOrFalse: number
}

const styles = {
	searchContainer: 'gap-[8px] mb-[32px] items-end flex-wrap',
	divider: 'mt-[32px] mb-[12px]',
}

function EditQuestionGenerator() {
	const dispatch = useAppDispatch()
	const navigate = useNavigate()
	const [getQuiz, { data: quiz }] = disciplineApi.useShowQuizMutationMutation()
	const { inputValue } = useAppSelector((state) => state.modal)
	const inputValueRef = useRef(inputValue)
	const [lastAction, setLastAction] = useState<'approve' | 'reject' | null>(
		null
	)

	useEffect(() => {
		inputValueRef.current = inputValue
	}, [inputValue])
	const { id } = useParams<{
		id: string
	}>()

	const [subject, setSubject] = useState<
		Array<{ option: string; value: number }>
	>([])

	const [discipline, setDiscipline] = useState<
		Array<{ option: string; value: number }>
	>([])

	const [subSubject, setSubSubject] = useState<
		Array<{ option: string; value: number }>
	>([])

	const [year, setYear] = useState<Array<{ option: string; value: number }>>([])

	const [occupation, setOccupation] = useState<
		Array<{ option: string; value: number }>
	>([])

	const [banca, setBanca] = useState<Array<{ option: string; value: number }>>(
		[]
	)

	const [institution, setInstitution] = useState<
		Array<{ option: string; value: number }>
	>([])

	const [disciplineId, setDisciplineId] = useState<string>()
	const [subjectId, setSubjectId] = useState<string>()
	const [enunciado, setEnunciado] = useState(quiz?.description)
	const [comment, setComment] = useState(quiz?.explanation)

	const baseUrl = `/questoes/criar-questao`

	const allPages = useMemo(() => {
		return [createBreadcrumbLink(`Cadastro Questão`, baseUrl, 1)]
	}, [baseUrl])

	const pages = useCreateBreadcrumb(allPages)

	const {
		register,
		handleSubmit,
		reset,
		setValue,
		formState: { errors, touchedFields },
		control,
	} = useForm<Inputs>({
		mode: 'onBlur',
		resolver: yupResolver(addQuizSchema),
	})

	const [editQuiz, { isSuccess }] = disciplineApi.useEditQuizMutation()

	const modalOptions = {
		title: 'Motivo',
		inputPlaceholder: 'Digite aqui o motivo',
		buttonLabel: 'Confirmar',
	}

	const { data: dataQuestions } = questionGeneratorApi.useListQuery({
		page: 1,
		limit: 10000,
	})

	const [approveRejectQuestion, { isSuccess: isSuccessApproveReject }] =
		questionGeneratorApi.useApproveRejectMutation()

	useEffect(() => {
		if (isSuccessApproveReject) {
			if (lastAction === 'approve') {
				dispatch(
					notificationCreators.show(
						successMessages.storedGenerateQuestionApproved
					)
				)
			} else if (lastAction === 'reject') {
				dispatch(
					notificationCreators.show(
						successMessages.storedGenerateQuestionReject
					)
				)
			}
			setLastAction(null)
		}
	}, [isSuccessApproveReject, lastAction, dispatch])

	const questionsIds = dataQuestions?.data.map((questions) => questions.id)

	const approveQuestion = () => {
		setLastAction('approve')
		if (questionsIds) {
			approveRejectQuestion({
				isApproved: true,
				questionIds: [Number(id)],
			})
			const currentQuestion = questionsIds.indexOf(Number(id))
			const nextQuestion = currentQuestion + 1

			if (nextQuestion < questionsIds.length) {
				navigate(`/revisar-questoes/${questionsIds[nextQuestion]}`)
			}
		}
	}

	const onSubmit: SubmitHandler<Inputs> = (data) => {
		const dataQuiz = {
			id: Number(id),
			title: data.title,
			description: data.enunciado,
			explanation: data.comment,
			isTrue: !!data.trueOrFalse,
			disciplineIds: data.disciplineSelect.map((v) => v.value as number),
			matterIds: data.subjectSelect.map((v) => v.value as number),
			submatterIds: data.subSubjectSelect.map((v) => v.value as number) || [],
			yearIds: data.yearSelect?.map((v) => v.value as number) || [],
			positionIds: data.occupationSelect?.map((v) => v.value as number) || [],
			juryIds: data.bancaSelect?.map((v) => v.value as number) || [],
			institutionIds:
				data.institutionSelect?.map((v) => v.value as number) || [],
		}

		editQuiz(dataQuiz)
		reset()
	}

	useEffect(() => {
		if (id) {
			getQuiz({ id: Number(id) as number })
		}
	}, [id, getQuiz])

	useEffect(() => {
		const newDiscipline: typeof discipline = []
		const newSubSubject: typeof subSubject = []
		const newSubject: typeof subject = []
		const newYear: typeof year = []
		const newOccupation: typeof occupation = []
		const newBanca: typeof banca = []
		const newIntitution: typeof institution = []

		if (quiz?.subMatters && quiz.subMatters.length > 0) {
			quiz.subMatters.forEach((e) => {
				newSubSubject.push({
					option: e.name,
					value: e.id as number,
				})

				newSubject.push({
					option: e.matter.name,
					value: e.matter.id as number,
				})

				newDiscipline.push({
					option: e.matter.discipline.name,
					value: e.matter.discipline.id as number,
				})
			})
		}

		if (quiz?.filters.yearFilters && quiz.filters.yearFilters.length > 0) {
			quiz.filters.yearFilters.forEach((e) => {
				newYear.push({
					option: e.name,
					value: e.id as number,
				})
			})
		}

		if (
			quiz?.filters.positionFilters &&
			quiz.filters.positionFilters.length > 0
		) {
			quiz.filters.positionFilters.forEach((e) => {
				newOccupation.push({
					option: e.name,
					value: e.id as number,
				})
			})
		}

		if (quiz?.filters.juryFilters && quiz.filters.juryFilters.length > 0) {
			quiz.filters.juryFilters.forEach((e) => {
				newBanca.push({
					option: e.name,
					value: e.id as number,
				})
			})
		}

		if (
			quiz?.filters.institutionFilters &&
			quiz.filters.institutionFilters.length > 0
		) {
			quiz.filters.institutionFilters.forEach((e) => {
				newIntitution.push({
					option: e.name,
					value: e.id as number,
				})
			})
		}
		setDiscipline(newDiscipline)
		setSubject(newSubject)
		setSubSubject(newSubSubject)
		setValue('trueOrFalse', quiz?.isTrue ? quiz?.isTrue : 0)
		setYear(newYear)
		setOccupation(newOccupation)
		setBanca(newBanca)
		setInstitution(newIntitution)
	}, [quiz, setValue])

	useEffect(() => {
		setValue(
			'disciplineSelect',
			discipline?.map((e) => ({ value: e.value, label: e.option }))
		)
		setValue(
			'subjectSelect',
			subject?.map((e) => ({ value: e.value, label: e.option }))
		)
		setValue(
			'subSubjectSelect',
			subSubject?.map((e) => ({ value: e.value, label: e.option }))
		)
		setValue(
			'yearSelect',
			year?.map((e) => ({ value: e.value, label: e.option }))
		)
		setValue(
			'occupationSelect',
			occupation?.map((e) => ({ value: e.value, label: e.option }))
		)
		setValue(
			'bancaSelect',
			banca?.map((e) => ({ value: e.value, label: e.option }))
		)
		setValue(
			'institutionSelect',
			institution?.map((e) => ({ value: e.value, label: e.option }))
		)
		if (quiz) {
			setValue('title', quiz.title)
			setEnunciado(quiz?.description)
			setComment(quiz?.explanation)
			setValue('enunciado', quiz.description)
			setValue('comment', quiz.explanation)
			setDisciplineId(discipline?.map((e) => e.value).join(','))
			setSubjectId(subject?.map((e) => e.value).join(','))
		}
	}, [
		banca,
		institution,
		occupation,
		quiz,
		setValue,
		subSubject,
		year,
		discipline,
		subject,
	])

	useEffect(() => {
		if (isSuccess) {
			navigate(`/questoes`)
			dispatch(notificationCreators.show(successMessages.updatedQuiz))
		}
	}, [isSuccess, dispatch, navigate, id])

	const { data: dataDiscipline } = disciplineApi.useListQuery({
		page: 1,
		search: '',
		limit: 100000,
	})

	const { data: dataSubject } = filtersApi.useListMatterQuery({
		disciplineId: disciplineId || '-1',
	})

	const { data: dataSubSubject } = filtersApi.useListSubMatterQuery({
		matterId: subjectId || '-1',
	})

	const { data: dataYear } = yearApi.useListQuery({
		page: 1,
		search: '',
		limit: 100000,
	})
	const { data: dataOccupation } = occupationApi.useListQuery({
		page: 1,
		search: '',
		limit: 100000,
	})

	const { data: dataBanca } = bancaApi.useListQuery({
		page: 1,
		search: '',
		limit: 100000,
	})

	const { data: dataInstitution } = institutionApi.useListQuery({
		page: 1,
		search: '',
		limit: 100000,
	})

	const typeOptions = useMemo(
		() => [
			{ option: 'VERDADEIRO', value: 1 },
			{ option: 'FALSO', value: 0 },
		],
		[]
	)
	const onTypeChange = useCallback(
		(v: Option) => setValue('trueOrFalse', Number(v.value)),
		[setValue]
	)

	return (
		<>
			<Template>
				<Breadcrumb pages={pages} />
				<PageTitle title='Edição de Questão' />

				<form onSubmit={handleSubmit(onSubmit)} className='mt-6'>
					<div className='flex gap-3 mb-5 justify-end'>
						<Button onClick={approveQuestion}>Aprovar</Button>

						<Button
							variant='danger'
							onClick={() =>
								dispatch(
									openInputModal({
										...modalOptions,
										onClick: () => {
											setLastAction('reject')
											approveRejectQuestion({
												isApproved: false,
												questionIds: [Number(id)],
												refuseReason: inputValueRef.current,
											})
										},
									})
								)
							}
						>
							Reprovar
						</Button>
					</div>
					<div className='grid grid-cols-2 gap-4'>
						<InputGroup label='Disciplina' id='disciplineSelect'>
							<Controller
								control={control}
								name='disciplineSelect'
								render={({
									field: { value, onBlur },
									fieldState: { isTouched, error },
								}) => {
									return (
										<Select
											onChange={(v) => {
												setDisciplineId(v?.map((val) => val.value).join(','))
												setValue('disciplineSelect', v)
											}}
											value={value}
											options={
												dataDiscipline?.data
													? dataDiscipline.data.map((item) => ({
															value: item.id,
															label: item.name,
													  }))
													: []
											}
											defaultValue={
												discipline.length > 0
													? discipline.map(
															({ option, value: disciplineSelectId }) => ({
																value: disciplineSelectId as number,
																label: option,
															})
													  )
													: undefined
											}
											isMulti
											onBlur={onBlur}
											styles={{
												input: (base) => ({
													...base,
													borderRadius: '6px',
													paddingLeft: '2.5px',
													paddingRight: '2.5px',
													paddingTop: '3.5px',
													paddingBottom: '3.5px',
												}),
												control: (controlStyles) => ({
													...controlStyles,
													borderColor: (() => {
														const isValid = inputIsValid(error, isTouched)

														if (typeof isValid === 'undefined') {
															return '#D1D5DB !important'
														}

														if (!isValid) {
															return '#F44545 !important'
														}

														return '#15CA71 !important'
													})(),
												}),
												option: (optionStyle, { isFocused }) => ({
													...optionStyle,
													backgroundColor: isFocused ? '#1673FA' : '#fff',
													color: isFocused ? '#fff' : '#000',
												}),
											}}
											placeholder=''
											classNamePrefix='react-select'
										/>
									)
								}}
							/>
						</InputGroup>
						<InputGroup label='Assunto' id='subjectSelect'>
							<Controller
								control={control}
								name='subjectSelect'
								render={({
									field: { value, onBlur },
									fieldState: { isTouched, error },
								}) => {
									return (
										<Select
											onChange={(v) => {
												setSubjectId(v?.map((val) => val.value).join(','))
												setValue('subjectSelect', v)
											}}
											value={value}
											options={
												dataSubject?.data
													? dataSubject.data.map((item) => ({
															value: item.id,
															label: item.name,
													  }))
													: []
											}
											defaultValue={
												subject.length > 0
													? subject.map(({ option, value: matterId }) => ({
															value: matterId as number,
															label: option,
													  }))
													: undefined
											}
											isMulti
											onBlur={onBlur}
											styles={{
												input: (base) => ({
													...base,
													borderRadius: '6px',
													paddingLeft: '2.5px',
													paddingRight: '2.5px',
													paddingTop: '3.5px',
													paddingBottom: '3.5px',
												}),
												control: (controlStyles) => ({
													...controlStyles,
													borderColor: (() => {
														const isValid = inputIsValid(error, isTouched)

														if (typeof isValid === 'undefined') {
															return '#D1D5DB !important'
														}

														if (!isValid) {
															return '#F44545 !important'
														}

														return '#15CA71 !important'
													})(),
												}),
												option: (optionStyle, { isFocused }) => ({
													...optionStyle,
													backgroundColor: isFocused ? '#1673FA' : '#fff',
													color: isFocused ? '#fff' : '#000',
												}),
											}}
											placeholder=''
											classNamePrefix='react-select'
										/>
									)
								}}
							/>
						</InputGroup>
					</div>
					<div className='grid grid-cols-2 gap-4'>
						<InputGroup label='Sub Assunto' id='subSubjectSelect'>
							<Controller
								control={control}
								name='subSubjectSelect'
								render={({
									field: { value, onBlur },
									fieldState: { isTouched, error },
								}) => {
									return (
										<Select
											onChange={(v) => setValue('subSubjectSelect', v)}
											options={
												dataSubSubject?.data
													? dataSubSubject.data.map((item) => ({
															value: item.id,
															label: item.name,
													  }))
													: []
											}
											value={value}
											defaultValue={
												subSubject.length > 0
													? subSubject.map(
															({ option, value: submatterId }) => ({
																value: submatterId as number,
																label: option,
															})
													  )
													: undefined
											}
											isMulti
											onBlur={onBlur}
											styles={{
												input: (base) => ({
													...base,
													borderRadius: '6px',
													paddingLeft: '2.5px',
													paddingRight: '2.5px',
													paddingTop: '3.5px',
													paddingBottom: '3.5px',
												}),
												control: (controlStyles) => ({
													...controlStyles,
													borderColor: (() => {
														const isValid = inputIsValid(error, isTouched)

														if (typeof isValid === 'undefined') {
															return '#D1D5DB !important'
														}

														if (!isValid) {
															return '#F44545 !important'
														}

														return '#15CA71 !important'
													})(),
												}),
												option: (optionStyle, { isFocused }) => ({
													...optionStyle,
													backgroundColor: isFocused ? '#1673FA' : '#fff',
													color: isFocused ? '#fff' : '#000',
												}),
											}}
											placeholder=''
											classNamePrefix='react-select'
										/>
									)
								}}
							/>
						</InputGroup>
						<InputGroup label='Ano' id='yearSelect'>
							<Controller
								control={control}
								name='yearSelect'
								render={({
									field: { value, onBlur },
									fieldState: { isTouched, error },
								}) => {
									return (
										<Select
											onChange={(v) => setValue('yearSelect', v)}
											options={
												dataYear?.data
													? dataYear.data.map((item) => ({
															value: item.id,
															label: item.name,
													  }))
													: []
											}
											value={value}
											isMulti
											onBlur={onBlur}
											styles={{
												input: (base) => ({
													...base,
													borderRadius: '6px',
													paddingLeft: '2.5px',
													paddingRight: '2.5px',
													paddingTop: '3.5px',
													paddingBottom: '3.5px',
												}),
												control: (controlStyles) => ({
													...controlStyles,
													borderColor: (() => {
														const isValid = inputIsValid(error, isTouched)

														if (typeof isValid === 'undefined') {
															return '#D1D5DB !important'
														}

														if (!isValid) {
															return '#F44545 !important'
														}

														return '#15CA71 !important'
													})(),
												}),
												option: (optionStyle, { isFocused }) => ({
													...optionStyle,
													backgroundColor: isFocused ? '#1673FA' : '#fff',
													color: isFocused ? '#fff' : '#000',
												}),
											}}
											placeholder=''
											classNamePrefix='react-select'
										/>
									)
								}}
							/>
						</InputGroup>
						<InputGroup label='Cargo' id='occupationSelect'>
							<Controller
								control={control}
								name='occupationSelect'
								render={({
									field: { value, onBlur },
									fieldState: { isTouched, error },
								}) => {
									return (
										<Select
											onChange={(v) => setValue('occupationSelect', v)}
											options={
												dataOccupation?.data
													? dataOccupation.data.map((item) => ({
															value: item.id,
															label: item.name,
													  }))
													: []
											}
											value={value}
											isMulti
											onBlur={onBlur}
											styles={{
												input: (base) => ({
													...base,
													borderRadius: '6px',
													paddingLeft: '2.5px',
													paddingRight: '2.5px',
													paddingTop: '3.5px',
													paddingBottom: '3.5px',
												}),
												control: (controlStyles) => ({
													...controlStyles,
													borderColor: (() => {
														const isValid = inputIsValid(error, isTouched)

														if (typeof isValid === 'undefined') {
															return '#D1D5DB !important'
														}

														if (!isValid) {
															return '#F44545 !important'
														}

														return '#15CA71 !important'
													})(),
												}),
												option: (optionStyle, { isFocused }) => ({
													...optionStyle,
													backgroundColor: isFocused ? '#1673FA' : '#fff',
													color: isFocused ? '#fff' : '#000',
												}),
											}}
											placeholder=''
											classNamePrefix='react-select'
										/>
									)
								}}
							/>
						</InputGroup>
						<InputGroup label='Banca' id='bancaSelect'>
							<Controller
								control={control}
								name='bancaSelect'
								render={({
									field: { value, onBlur },
									fieldState: { isTouched, error },
								}) => {
									return (
										<Select
											onChange={(v) => setValue('bancaSelect', v)}
											options={
												dataBanca?.data
													? dataBanca.data.map((item) => ({
															value: item.id,
															label: item.name,
													  }))
													: []
											}
											value={value}
											isMulti
											onBlur={onBlur}
											styles={{
												input: (base) => ({
													...base,
													borderRadius: '6px',
													paddingLeft: '2.5px',
													paddingRight: '2.5px',
													paddingTop: '3.5px',
													paddingBottom: '3.5px',
												}),
												control: (controlStyles) => ({
													...controlStyles,
													borderColor: (() => {
														const isValid = inputIsValid(error, isTouched)

														if (typeof isValid === 'undefined') {
															return '#D1D5DB !important'
														}

														if (!isValid) {
															return '#F44545 !important'
														}

														return '#15CA71 !important'
													})(),
												}),
												option: (optionStyle, { isFocused }) => ({
													...optionStyle,
													backgroundColor: isFocused ? '#1673FA' : '#fff',
													color: isFocused ? '#fff' : '#000',
												}),
											}}
											placeholder=''
											classNamePrefix='react-select'
										/>
									)
								}}
							/>
						</InputGroup>
						<InputGroup label='Instituição' id='institutionSelect'>
							<Controller
								control={control}
								name='institutionSelect'
								render={({
									field: { value, onBlur },
									fieldState: { isTouched, error },
								}) => {
									return (
										<Select
											onChange={(v) => setValue('institutionSelect', v)}
											options={
												dataInstitution?.data
													? dataInstitution.data.map((item) => ({
															value: item.id,
															label: item.name,
													  }))
													: []
											}
											value={value}
											isMulti
											onBlur={onBlur}
											styles={{
												input: (base) => ({
													...base,
													borderRadius: '6px',
													paddingLeft: '2.5px',
													paddingRight: '2.5px',
													paddingTop: '3.5px',
													paddingBottom: '3.5px',
												}),
												control: (controlStyles) => ({
													...controlStyles,
													borderColor: (() => {
														const isValid = inputIsValid(error, isTouched)

														if (typeof isValid === 'undefined') {
															return '#D1D5DB !important'
														}

														if (!isValid) {
															return '#F44545 !important'
														}

														return '#15CA71 !important'
													})(),
												}),
												option: (optionStyle, { isFocused }) => ({
													...optionStyle,
													backgroundColor: isFocused ? '#1673FA' : '#fff',
													color: isFocused ? '#fff' : '#000',
												}),
											}}
											placeholder=''
											classNamePrefix='react-select'
										/>
									)
								}}
							/>
						</InputGroup>
						<InputGroup label='Título' id='title'>
							<Input
								type='text'
								{...register('title')}
								isValid={inputIsValid(errors.title, touchedFields.title)}
							/>
						</InputGroup>
					</div>

					<Controller
						{...register('enunciado')}
						name='enunciado'
						control={control}
						render={({ field: { onBlur } }) => (
							<InputGroup
								className={`mt-6 index-0-controler ${
									errors.enunciado && 'error-textBox'
								}`}
								label='Enunciado'
								id='enunciado'
							>
								<TextEditor
									onChange={(e) => {
										setEnunciado(e)
										setValue('enunciado', e)
									}}
									value={enunciado}
									onBlur={onBlur}
								/>
							</InputGroup>
						)}
					/>

					<Controller
						{...register('comment')}
						name='comment'
						control={control}
						render={({ field: { onBlur } }) => (
							<InputGroup
								className={`mt-6 ${errors.comment && 'error-textBox'}`}
								label='Comentário'
								id='comment'
							>
								<TextEditor
									onChange={(e) => {
										setComment(e)
										setValue('comment', e)
									}}
									value={comment}
									onBlur={onBlur}
								/>
							</InputGroup>
						)}
					/>

					<div className='flex justify-start max-w-[50%]'>
						<InputGroup
							className='mt-6'
							label='Verdadeiro/Falso'
							id='trueOrFalse'
						>
							<SelectInput
								btnClass='px-[13px] py-[8px] w-[150px]'
								options={typeOptions}
								selectedValue={quiz?.isTrue}
								onChange={onTypeChange}
							/>
						</InputGroup>
					</div>

					<div className='flex gap-4 mt-6 mb-10'>
						<Button variant='outline' onClick={() => navigate(`/questoes`)}>
							Cancelar
						</Button>
						<Button type='submit'>Editar Questão</Button>
					</div>
				</form>
				<Divider className={styles.divider} />
			</Template>
		</>
	)
}

export default EditQuestionGenerator
